<template>
  <div class="transfer-container">
    <!-- 账户余额卡片 -->
    <div class="account-cards">
      <div 
        v-for="account in accounts" 
        :key="account.type"
        class="account-card"
        :class="{ 'active': selectedFromAccount === account.type || selectedToAccount === account.type }"
      >
        <div class="card-header">
          <div class="account-icon" :style="{ background: account.color }">
            <i :class="account.icon"></i>
          </div>
          <div class="account-info">
            <div class="account-name">{{ $t(account.nameKey) || account.name }}</div>
            <!-- 只在余额加载完成后显示，否则显示加载动画 -->
            <div v-if="balancesLoaded" class="account-balance">{{ formatNumber(account.balance) }} USD</div>
            <div v-else class="account-balance loading-placeholder">
              <span class="loading-dot"></span>
              <span class="loading-dot"></span>
              <span class="loading-dot"></span>
            </div>
          </div>
        </div>
        <div class="card-footer">
          <span class="label">{{ $t('transfer.available') || '可用' }}:</span>
          <!-- 只在余额加载完成后显示 -->
          <span v-if="balancesLoaded" class="available">{{ formatNumber(account.available) }} USD</span>
          <span v-else class="available loading-placeholder">
            <span class="loading-dot"></span>
            <span class="loading-dot"></span>
            <span class="loading-dot"></span>
          </span>
        </div>
      </div>
    </div>

    <!-- 划转表单 -->
    <div class="transfer-form">
      <div class="form-title">{{ $t('transfer.title') || '账户划转' }}</div>
      
      <div class="form-group">
        <label>{{ $t('transfer.fromAccount') || '转出账户' }}</label>
        <div class="account-selector">
          <div 
            v-for="account in accounts" 
            :key="'from-' + account.type"
            class="selector-item"
            :class="{ 'selected': selectedFromAccount === account.type, 'disabled': selectedToAccount === account.type }"
            @click="selectFromAccount(account.type)"
          >
            <i :class="account.icon"></i>
            <span>{{ $t(account.nameKey) || account.name }}</span>
          </div>
        </div>
      </div>

      <div class="transfer-arrow">
        <i class="el-icon-bottom"></i>
      </div>

      <div class="form-group">
        <label>{{ $t('transfer.toAccount') || '转入账户' }}</label>
        <div class="account-selector">
          <div 
            v-for="account in accounts" 
            :key="'to-' + account.type"
            class="selector-item"
            :class="{ 'selected': selectedToAccount === account.type, 'disabled': selectedFromAccount === account.type }"
            @click="selectToAccount(account.type)"
          >
            <i :class="account.icon"></i>
            <span>{{ $t(account.nameKey) || account.name }}</span>
          </div>
        </div>
      </div>

      <div class="form-group">
        <label>{{ $t('transfer.amount') || '划转金额' }}</label>
        <div class="amount-input-wrapper">
          <input 
            v-model="transferAmount" 
            type="number" 
            class="amount-input"
            :placeholder="$t('transfer.enterAmount') || '请输入划转金额'"
            @input="validateAmount"
          />
          <button class="all-btn" @click="transferAll">{{ $t('transfer.all') || '全部' }}</button>
        </div>
        <div v-if="selectedFromAccount" class="available-hint">
          {{ $t('transfer.availableBalance') || '可用余额' }}: {{ getAvailableBalance(selectedFromAccount) }} USD
        </div>
      </div>

      <button class="transfer-btn" @click="handleTransfer" :disabled="!canTransfer">
        {{ $t('transfer.confirm') || '确认划转' }}
      </button>
    </div>

    <!-- 划转记录 -->
    <div class="transfer-history" v-if="showHistory">
      <div class="history-header">
        <div class="history-title">{{ $t('transfer.history') || '划转记录' }}</div>
        <button class="refresh-btn" @click="fetchTransferHistory">
          <i class="el-icon-refresh"></i>
        </button>
      </div>
      
      <div class="history-list" v-if="transferRecords.length > 0">
        <div v-for="record in transferRecords" :key="record.id" class="history-item">
          <div class="item-header">
            <div class="transfer-direction">
              <span class="from">{{ getAccountName(record.fromAccountType) }}</span>
              <i class="el-icon-right"></i>
              <span class="to">{{ getAccountName(record.toAccountType) }}</span>
            </div>
            <div class="status" :class="'status-' + record.status">
              {{ getStatusText(record.status) }}
            </div>
          </div>
          <div class="item-body">
            <div class="amount">{{ formatNumber(record.transferAmount) }} USD</div>
            <div class="time">{{ formatTime(record.createTime) }}</div>
          </div>
        </div>
      </div>

      <div v-else class="empty-state">
        <i class="el-icon-document"></i>
        <p>{{ $t('transfer.noRecords') || '暂无划转记录' }}</p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AccountTransfer',
  props: {
    showHistory: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      accounts: [
        {
          type: 1,
          name: '普通账户',
          nameKey: 'transfer.spotAccount',
          icon: 'el-icon-wallet',
          color: 'linear-gradient(135deg, var(--primary-dark) 0%, var(--primary-color) 100%)',
          balance: null,
          frozen: null,
          available: null
        },
        {
          type: 2,
          name: '合约账户',
          nameKey: 'transfer.contractAccount',
          icon: 'el-icon-files',
          color: 'linear-gradient(135deg, var(--primary-color) 0%, var(--primary-light) 100%)',
          balance: null,
          frozen: null,
          available: null
        },
        {
          type: 3,
          name: '期权账户',
          nameKey: 'transfer.optionAccount',
          icon: 'el-icon-document-checked',
          color: 'linear-gradient(135deg, var(--primary-light) 0%, var(--primary-color) 100%)',
          balance: null,
          frozen: null,
          available: null
        }
      ],
      selectedFromAccount: null,
      selectedToAccount: null,
      transferAmount: '',
      transferRecords: [],
      loading: false,
      balancesLoaded: false // 余额是否已加载
    };
  },
  computed: {
    canTransfer() {
      return this.balancesLoaded &&
             this.selectedFromAccount && 
             this.selectedToAccount && 
             this.transferAmount && 
             parseFloat(this.transferAmount) > 0 &&
             this.selectedFromAccount !== this.selectedToAccount;
    }
  },
  mounted() {
    this.fetchAccountBalances();
    if (this.showHistory) {
      this.fetchTransferHistory();
    }
  },
  methods: {
    async fetchAccountBalances() {
      try {
        const res = await this.$http.get('/swx/customer/account/balances');
        if (res.code === 200 && res.result) {
          const data = res.result;
          
          // 更新普通账户
          this.accounts[0].balance = parseFloat(data.spotBalance || 0);
          this.accounts[0].frozen = parseFloat(data.spotFrozen || 0);
          this.accounts[0].available = parseFloat(data.spotAvailable || 0);
          
          // 更新合约账户
          this.accounts[1].balance = parseFloat(data.contractBalance || 0);
          this.accounts[1].frozen = parseFloat(data.contractFrozen || 0);
          this.accounts[1].available = parseFloat(data.contractAvailable || 0);
          
          // 更新期权账户
          this.accounts[2].balance = parseFloat(data.optionBalance || 0);
          this.accounts[2].frozen = parseFloat(data.optionFrozen || 0);
          this.accounts[2].available = parseFloat(data.optionAvailable || 0);
          
          // 标记余额已加载
          this.balancesLoaded = true;
        }
      } catch (error) {
        console.error('获取账户余额失败:', error);
        // 即使失败也标记为已加载，避免一直显示加载状态
        this.balancesLoaded = true;
      }
    },
    
    async fetchTransferHistory() {
      try {
        const res = await this.$http.get('/swx/customer/account/transfer/history', {
          params: {
            pageNum: 1,
            pageSize: 20
          }
        });
        if (res.code === 200 && res.result) {
          this.transferRecords = res.result.list || [];
        }
      } catch (error) {
        console.error('获取划转记录失败:', error);
      }
    },
    
    selectFromAccount(type) {
      if (type !== this.selectedToAccount) {
        this.selectedFromAccount = type;
      }
    },
    
    selectToAccount(type) {
      if (type !== this.selectedFromAccount) {
        this.selectedToAccount = type;
      }
    },
    
    validateAmount() {
      const amount = parseFloat(this.transferAmount);
      if (isNaN(amount) || amount < 0) {
        this.transferAmount = '';
      }
    },
    
    transferAll() {
      if (this.selectedFromAccount && this.balancesLoaded) {
        const account = this.accounts.find(a => a.type === this.selectedFromAccount);
        if (account && account.available !== null) {
          this.transferAmount = account.available;
        }
      }
    },
    
    getAvailableBalance(accountType) {
      const account = this.accounts.find(a => a.type === accountType);
      // 如果余额还未加载，返回 '--'
      if (!account || account.available === null) {
        return '--';
      }
      return this.formatNumber(account.available);
    },
    
    getAccountName(accountType) {
      const account = this.accounts.find(a => a.type === accountType);
      return account ? (this.$t(account.nameKey) || account.name) : '';
    },
    
    async handleTransfer() {
      if (!this.canTransfer) return;
      
      const amount = parseFloat(this.transferAmount);
      const fromAccount = this.accounts.find(a => a.type === this.selectedFromAccount);
      
      if (amount > fromAccount.available) {
        this.$toast.error(this.$t('transfer.insufficientBalance') || '可用余额不足');
        return;
      }
      
      this.loading = true;
      
      try {
        const res = await this.$http.post('/swx/customer/account/transfer', {
          fromAccountType: this.selectedFromAccount,
          toAccountType: this.selectedToAccount,
          amount: amount
        });
        
        if (res.code === 200) {
          this.$toast.success(this.$t('transfer.success') || '划转成功');
          
          // 重置表单
          this.transferAmount = '';
          this.selectedFromAccount = null;
          this.selectedToAccount = null;
          
          // 刷新数据
          await this.fetchAccountBalances();
          if (this.showHistory) {
            await this.fetchTransferHistory();
          }
          
          this.$emit('transfer-success');
        } else {
          this.$toast.error(res.message || (this.$t('transfer.failed') || '划转失败'));
        }
      } catch (error) {
        this.$toast.error(this.$t('transfer.error') || '划转失败，请稍后重试');
        console.error('划转失败:', error);
      } finally {
        this.loading = false;
      }
    },
    
    formatNumber(num) {
      return parseFloat(num || 0);
    },
    
    formatTime(time) {
      if (!time) return '';
      const date = new Date(time);
      return date.toLocaleString();
    },
    
    getStatusText(status) {
      const statusMap = {
        0: this.$t('transfer.statusFailed') || '失败',
        1: this.$t('transfer.statusSuccess') || '成功',
        2: this.$t('transfer.statusPending') || '处理中'
      };
      return statusMap[status] || '';
    }
  }
};
</script>

<style scoped>
.transfer-container {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

/* 账户卡片 */
.account-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 30px;
}

.account-card {
  background: var(--bg-secondary); /* ✅ 改为灰色背景 */
  border-radius: 16px;
  padding: 20px;
  /* ✅ 删除阴影 */
  transition: all 0.3s ease;
  border: none; /* ✅ 删除边框 */
  display: flex;
  flex-direction: column;
  height: 100%; /* 确保高度填满 grid 单元格 */
  min-height: 140px; /* 确保最小高度一致 */
}

.account-card:hover,
.account-card.active {
  /* ✅ 删除hover和active状态的阴影和边框 */
  transform: translateY(-2px);
}

.card-header {
  display: flex;
  align-items: flex-start; /* 改为顶部对齐，确保账户名称在同一高度 */
  gap: 15px;
  flex: 1; /* 占据剩余空间 */
  min-height: 70px; /* 确保最小高度 */
}

.account-icon {
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 24px;
  flex-shrink: 0; /* 防止被压缩 */
}

.account-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-start; /* 改为顶部对齐 */
  padding-top: 2px; /* 微调与图标对齐 */
  min-height: 50px; /* 确保最小高度一致 */
}

.account-name {
  font-size: 14px;
  color: var(--text-secondary);
  margin-bottom: 8px; /* 增加间距 */
  line-height: 1.4;
  height: 20px; /* 固定高度，确保对齐 */
}

.account-balance {
  font-size: 24px;
  font-weight: 600;
  color: var(--text-primary);
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
  line-height: 1.2;
  word-break: break-all;
}

/* 加载占位符 */
.loading-placeholder {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.loading-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--text-secondary);
  opacity: 0.6;
  animation: loadingDot 1.4s infinite ease-in-out both;
}

.loading-dot:nth-child(1) {
  animation-delay: -0.32s;
}

.loading-dot:nth-child(2) {
  animation-delay: -0.16s;
}

@keyframes loadingDot {
  0%, 80%, 100% {
    transform: scale(0.8);
    opacity: 0.3;
  }
  40% {
    transform: scale(1.2);
    opacity: 1;
  }
}

.card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto; /* 自动推到底部 */
  padding-top: 15px;
  /* ✅ 删除顶部边框线 */
}

.card-footer .label {
  font-size: 13px;
  color: var(--text-secondary);
  white-space: nowrap;
  flex-shrink: 0;
}

.card-footer .available {
  font-size: 15px;
  font-weight: 600;
  color: var(--primary-color, #0156DC);
  text-align: right;
  flex: 1;
  min-width: 0;
  font-variant-numeric: tabular-nums;
  font-feature-settings: "tnum";
}

/* 划转表单 */
.transfer-form {
  background: var(--card-bg);
  border-radius: 16px;
  padding: 30px;
  /* ✅ 删除边框阴影 */
  margin-bottom: 30px;
}

.form-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 25px;
}

.form-group {
  margin-bottom: 25px;
}

.form-group label {
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 10px;
}

.account-selector {
  display: flex;
  gap: 10px;
}

.selector-item {
  flex: 1;
  padding: 15px;
  background: var(--bg-secondary);
  border: none; /* ✅ 删除边框 */
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.selector-item i {
  font-size: 24px;
  color: var(--text-secondary);
}

.selector-item span {
  font-size: 13px;
  color: var(--text-primary);
  font-weight: 500;
}

.selector-item:hover:not(.disabled) {
  background: var(--hover-bg);
  /* ✅ 删除hover边框 */
}

.selector-item.selected {
  background: var(--primary-color-10);
  /* ✅ 删除选中边框 */
}

.selector-item.selected i,
.selector-item.selected span {
  color: var(--primary-color, #0156DC);
}

.selector-item.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.transfer-arrow {
  text-align: center;
  margin: 15px 0;
}

.transfer-arrow i {
  font-size: 24px;
  color: var(--primary-color, #0156DC);
}

.amount-input-wrapper {
  display: flex;
  gap: 10px;
}

.amount-input {
  flex: 1;
  padding: 12px 15px;
  background: var(--bg-secondary);
  border: none; /* ✅ 删除边框 */
  border-radius: 12px;
  font-size: 16px;
  color: var(--text-primary);
  transition: all 0.3s ease;
}

.amount-input:focus {
  outline: none;
  /* ✅ 删除聚焦边框 */
  background: var(--card-bg);
}

.all-btn {
  padding: 12px 24px;
  background: var(--bg-secondary);
  border: none; /* ✅ 删除边框 */
  border-radius: 12px;
  color: var(--text-primary);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.all-btn:hover {
  background: var(--primary-color, #0156DC);
  color: white;
}

.available-hint {
  margin-top: 8px;
  font-size: 13px;
  color: var(--text-secondary);
}

.transfer-btn {
  width: 100%;
  padding: 15px;
  background: var(--primary-color, #0156DC);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.transfer-btn:hover:not(:disabled) {
  background: var(--primary-dark);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px var(--primary-color-20);
}

.transfer-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 划转记录 */
.transfer-history {
  background: var(--card-bg);
  border-radius: 16px;
  padding: 30px;
  /* ✅ 删除边框阴影 */
}

.history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.history-title {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
}

.refresh-btn {
  padding: 8px 12px;
  background: transparent;
  border: none; /* ✅ 删除边框 */
  border-radius: 8px;
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.3s ease;
}

.refresh-btn:hover {
  background: var(--hover-bg);
  color: var(--primary-color, #0156DC);
}

.history-list {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.history-item {
  padding: 15px;
  background: var(--bg-secondary);
  border-radius: 12px;
  /* ✅ 删除边框线 */
  transition: all 0.3s ease;
}

/* .history-item:hover - 已移除hover效果 */

.item-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.transfer-direction {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
}

.status {
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.status-1 {
  background: #f6ffed;
  color: #52c41a;
}

.status-0 {
  background: #fff1f0;
  color: #f5222d;
}

.status-2 {
  background: #e6f7ff;
  color: #1890ff;
}

.item-body {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.item-body .amount {
  font-size: 18px;
  font-weight: 600;
  color: var(--primary-color, #0156DC);
}

.item-body .time {
  font-size: 12px;
  color: var(--text-secondary);
}

.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: var(--text-secondary);
}

.empty-state i {
  font-size: 64px;
  color: var(--border-color);
  margin-bottom: 15px;
}

/* 响应式 */
/* 平板：2列 */
@media (max-width: 1024px) and (min-width: 769px) {
  .account-cards {
    grid-template-columns: repeat(2, 1fr);
  }
}

/* 移动端：1列 */
@media (max-width: 768px) {
  .transfer-container {
    padding: 15px;
  }

  .account-cards {
    grid-template-columns: 1fr;
    gap: 15px;
  }

  .account-selector {
    flex-direction: column;
  }

  .selector-item {
    flex-direction: row;
    justify-content: flex-start;
    padding: 12px 15px;
  }

  .transfer-form,
  .transfer-history {
    padding: 20px;
  }
}

/* 黑夜模式适配 */
.dark-mode .account-card {
  background: rgba(255, 255, 255, 0.05); /* ✅ 黑夜模式下使用半透明白色 */
}

.dark-mode .account-card:hover,
.dark-mode .account-card.active {
  background: rgba(255, 255, 255, 0.08);
}

.dark-mode .selector-item {
  background: rgba(255, 255, 255, 0.05);
}

.dark-mode .selector-item:hover:not(.disabled) {
  background: rgba(255, 255, 255, 0.1);
}

.dark-mode .selector-item.selected {
  background: rgba(var(--primary-color-rgb), 0.2);
}

.dark-mode .amount-input {
  background: rgba(255, 255, 255, 0.05);
  color: var(--text-primary);
}

.dark-mode .amount-input:focus {
  background: rgba(255, 255, 255, 0.08);
}

.dark-mode .all-btn {
  background: rgba(255, 255, 255, 0.05);
}

.dark-mode .history-item {
  background: rgba(255, 255, 255, 0.05);
}

.dark-mode .status-1 {
  background: rgba(82, 196, 26, 0.2);
  color: #95de64;
}

.dark-mode .status-0 {
  background: rgba(245, 34, 45, 0.2);
  color: #ff7875;
}

.dark-mode .status-2 {
  background: rgba(24, 144, 255, 0.2);
  color: #69c0ff;
}
</style>

